import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Shield, Check, Save, AlertTriangle } from 'lucide-react';
import axios from 'axios';

import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Label } from '../ui/label';
import { useAuth, API } from '../../contexts/AuthContext';


const getErrorMessage = (error, defaultMessage) => {
  return error.response?.data?.error || error.message || defaultMessage;
};

const PermissionsTab = () => {
  const { getAuthHeaders, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('waiter');
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [availableModules, setAvailableModules] = useState([]);
  const [roleConfig, setRoleConfig] = useState({
    permissions: [],
    modules_visible: []
  });

  // Roles disponibles (excluyendo super_admin y customer)
  const roles = [
    { id: 'waiter', name: 'Mesero', icon: '🧑‍🍳', color: 'blue' },
    { id: 'cashier', name: 'Cajero', icon: '💰', color: 'green' },
    { id: 'kitchen', name: 'Cocina', icon: '👨‍🍳', color: 'orange' },
    { id: 'delivery', name: 'Repartidor', icon: '🏍️', color: 'purple' },
    { id: 'admin', name: 'Administrador', icon: '👔', color: 'red' }
  ];

  useEffect(() => {
    loadAvailablePermissions();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      loadRolePermissions(selectedRole);
    }
  }, [selectedRole]);

  const loadAvailablePermissions = async () => {
    try {
      const response = await axios.get(`${API}/permissions/available`, {
        headers: getAuthHeaders()
      });
      setAvailablePermissions(response.data.permissions);
      setAvailableModules(response.data.modules);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar permisos disponibles'));
    }
  };

  const loadRolePermissions = async (role) => {
    try {
      const response = await axios.get(`${API}/permissions/roles/${role}`, {
        headers: getAuthHeaders()
      });
      setRoleConfig({
        permissions: response.data.permissions || [],
        modules_visible: response.data.modules_visible || []
      });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar permisos del rol'));
    }
  };

  const togglePermission = (permissionKey) => {
    const newPermissions = roleConfig.permissions.includes(permissionKey)
      ? roleConfig.permissions.filter(p => p !== permissionKey)
      : [...roleConfig.permissions, permissionKey];
    
    setRoleConfig({ ...roleConfig, permissions: newPermissions });
  };

  const toggleModule = (moduleId) => {
    const newModules = roleConfig.modules_visible.includes(moduleId)
      ? roleConfig.modules_visible.filter(m => m !== moduleId)
      : [...roleConfig.modules_visible, moduleId];
    
    setRoleConfig({ ...roleConfig, modules_visible: newModules });
  };

  const selectAllModulePermissions = (module) => {
    const modulePermissions = availablePermissions[module]?.map(p => p.key) || [];
    const otherPermissions = roleConfig.permissions.filter(
      p => !p.startsWith(`${module}:`)
    );
    
    setRoleConfig({
      ...roleConfig,
      permissions: [...otherPermissions, ...modulePermissions]
    });
  };

  const deselectAllModulePermissions = (module) => {
    const newPermissions = roleConfig.permissions.filter(
      p => !p.startsWith(`${module}:`)
    );
    
    setRoleConfig({ ...roleConfig, permissions: newPermissions });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/permissions/roles/${selectedRole}`,
        {
          permissions: roleConfig.permissions,
          modules_visible: roleConfig.modules_visible
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success('Permisos actualizados correctamente');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al actualizar permisos'));
    }
    setLoading(false);
  };

  const getModuleColor = (module) => {
    const colors = {
      orders: 'bg-blue-100 text-blue-700 border-blue-300',
      products: 'bg-green-100 text-green-700 border-green-300',
      customers: 'bg-purple-100 text-purple-700 border-purple-300',
      users: 'bg-red-100 text-red-700 border-red-300',
      reports: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      config: 'bg-gray-100 text-gray-700 border-gray-300',
      tables: 'bg-indigo-100 text-indigo-700 border-indigo-300',
      printers: 'bg-pink-100 text-pink-700 border-pink-300',
      payments: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      inventory: 'bg-orange-100 text-orange-700 border-orange-300',
      categories: 'bg-cyan-100 text-cyan-700 border-cyan-300',
      reservations: 'bg-teal-100 text-teal-700 border-teal-300'
    };
    return colors[module] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const selectedRoleData = roles.find(r => r.id === selectedRole);

  // Verificar si el usuario es super_admin o admin
  if (user?.role !== 'super_admin' && user?.role !== 'admin') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-8 h-8 text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Gestión de Permisos</h2>
            <p className="text-gray-600">Configura qué puede ver y hacer cada rol en tu restaurante</p>
          </div>
        </div>

        <Card className="p-8 bg-gradient-to-br from-orange-50 to-red-50 border-orange-200">
          <div className="flex flex-col items-center justify-center text-center py-12">
            <AlertTriangle className="w-20 h-20 text-orange-500 mb-4" />
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Acceso Restringido</h3>
            <p className="text-gray-600 max-w-md mb-6">
              Solo el <span className="font-bold text-orange-600">Super Administrador</span> y <span className="font-bold text-orange-600">Administrador</span> pueden configurar permisos y accesos de los roles del sistema.
            </p>
            <div className="bg-white border-2 border-orange-200 rounded-xl p-4 max-w-md">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Tu rol actual:</span> <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 font-medium">{user?.role}</span>
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Si necesitas modificar permisos, contacta al Super Administrador o Administrador de tu organización.
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="w-8 h-8 text-blue-600" />
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Gestión de Permisos</h2>
          <p className="text-gray-600">Configura qué puede ver y hacer cada rol en tu restaurante</p>
        </div>
      </div>

      {/* Selector de Rol */}
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <Label className="text-lg font-semibold mb-4 block">Seleccionar Rol</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {roles.map((role) => (
            <button
              key={role.id}
              onClick={() => setSelectedRole(role.id)}
              className={`p-4 rounded-xl border-2 transition-all ${
                selectedRole === role.id
                  ? 'border-blue-500 bg-blue-100 shadow-md scale-105'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-white'
              }`}
            >
              <div className="text-3xl mb-2">{role.icon}</div>
              <div className="font-semibold text-sm">{role.name}</div>
            </button>
          ))}
        </div>
      </Card>

      {selectedRoleData && (
        <>
          {/* Módulos Visibles */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Módulos Visibles para {selectedRoleData.name}
                </h3>
                <p className="text-sm text-gray-600">
                  Selecciona qué módulos podrá ver este rol en la interfaz
                </p>
              </div>
              <div className="text-sm text-gray-500">
                {roleConfig.modules_visible.length} / {availableModules.length} módulos
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {availableModules.map((module) => (
                <button
                  key={module.id}
                  onClick={() => toggleModule(module.id)}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    roleConfig.modules_visible.includes(module.id)
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      roleConfig.modules_visible.includes(module.id)
                        ? 'border-green-500 bg-green-500'
                        : 'border-gray-300'
                    }`}>
                      {roleConfig.modules_visible.includes(module.id) && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <span className="font-medium text-sm">{module.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </Card>

          {/* Permisos por Módulo */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Permisos Detallados para {selectedRoleData.name}
                </h3>
                <p className="text-sm text-gray-600">
                  Configura qué acciones puede realizar en cada módulo
                </p>
              </div>
              <div className="text-sm text-gray-500">
                {roleConfig.permissions.length} permisos activos
              </div>
            </div>

            <div className="space-y-4">
              {Object.keys(availablePermissions).map((module) => {
                const moduleInfo = availableModules.find(m => m.id === module);
                const modulePerms = availablePermissions[module];
                const activePerms = roleConfig.permissions.filter(p => p.startsWith(`${module}:`)).length;
                const allSelected = activePerms === modulePerms.length;

                return (
                  <div
                    key={module}
                    className={`border-2 rounded-xl p-4 ${getModuleColor(module)}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-lg">
                          {moduleInfo?.name || module}
                        </h4>
                        <span className="text-xs font-medium px-2 py-1 bg-white rounded-full">
                          {activePerms}/{modulePerms.length}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => allSelected 
                            ? deselectAllModulePermissions(module)
                            : selectAllModulePermissions(module)
                          }
                          className="text-xs"
                        >
                          {allSelected ? 'Desmarcar todos' : 'Marcar todos'}
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {modulePerms.map((perm) => {
                        const isActive = roleConfig.permissions.includes(perm.key);
                        return (
                          <button
                            key={perm.key}
                            onClick={() => togglePermission(perm.key)}
                            className={`p-2 rounded-lg border transition-all text-left ${
                              isActive
                                ? 'bg-white border-current shadow-sm'
                                : 'bg-white/50 border-transparent hover:border-current/30'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                isActive ? 'bg-current border-current' : 'border-current/30'
                              }`}>
                                {isActive && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <span className="text-sm font-medium">{perm.description}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Botón Guardar */}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => loadRolePermissions(selectedRole)}
              className="rounded-xl"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading}
              className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl px-8"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default PermissionsTab;
